import { useRef, useEffect, useState, useCallback } from 'react';
import s from '../../Assets/Styles/Joystick/VirtualJoystick.module.css';

const DEADZONE = 0.14;
const THROTTLE = 50;

export default function VirtualJoystick({ onMove }) {
  const baseRef  = useRef(null);
  const infoRef  = useRef({ active: false, cx: 0, cy: 0, radius: 0 });
  const joyRef   = useRef({ x: 0, y: 0 });
  const timerRef = useRef(null);
  const [thumb,  setThumb]  = useState({ x: 50, y: 50 });
  const [active, setActive] = useState(false);

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  const fire = useCallback(() => {
    timerRef.current = null;
    onMove({ ...joyRef.current });
  }, [onMove]);

  const update = useCallback((px, py) => {
    const { cx, cy, radius } = infoRef.current;
    const dx = px - cx, dy = py - cy;
    const d  = Math.sqrt(dx*dx + dy*dy);
    const r  = radius;
    const nm = Math.min(d, r);
    const a  = Math.atan2(dy, dx);
    const tx = cx + Math.cos(a) * nm;
    const ty = cy + Math.sin(a) * nm;
    const rect = baseRef.current.getBoundingClientRect();
    setThumb({
      x: clamp(((tx - rect.left) / rect.width)  * 100, 5, 95),
      y: clamp(((ty - rect.top)  / rect.height) * 100, 5, 95),
    });
    joyRef.current = { x: clamp(dx/r, -1, 1), y: clamp(dy/r, -1, 1) };
    if (!timerRef.current) timerRef.current = setTimeout(fire, THROTTLE);
  }, [fire]);

  // ✅ FIX: Cancel pending throttle timer FIRST, then send STOP immediately
  const reset = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setThumb({ x: 50, y: 50 });
    setActive(false);
    joyRef.current = { x: 0, y: 0 };
    onMove({ x: 0, y: 0 }); // immediate — no timer
  }, [onMove]);

  useEffect(() => {
    const el = baseRef.current;
    const xy = e => e.touches
      ? { px: e.touches[0].clientX, py: e.touches[0].clientY }
      : { px: e.clientX, py: e.clientY };

    const onDown = e => {
      e.preventDefault(); e.stopPropagation();
      const rect = el.getBoundingClientRect();
      infoRef.current = {
        active: true,
        cx: rect.left + rect.width  / 2,
        cy: rect.top  + rect.height / 2,
        radius: rect.width * 0.36,
      };
      setActive(true);
      const { px, py } = xy(e);
      update(px, py);
    };

    const onMove = e => {
      if (!infoRef.current.active) return;
      e.preventDefault(); e.stopPropagation();
      const { px, py } = xy(e);
      update(px, py);
    };

    const onUp = e => {
      if (!infoRef.current.active) return;
      e.preventDefault();
      infoRef.current.active = false;
      reset(); // ✅ calls clearTimeout + immediate STOP
    };

    el.addEventListener('mousedown',  onDown);
    el.addEventListener('touchstart', onDown, { passive: false });
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend',  onUp,   { passive: false });

    return () => {
      el.removeEventListener('mousedown',  onDown);
      el.removeEventListener('touchstart', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend',  onUp);
    };
  }, [update, reset]);

  const { x, y } = joyRef.current;
  let dir = 'IDLE';
  if (Math.abs(x) > DEADZONE || Math.abs(y) > DEADZONE) {
    if (Math.abs(y) >= Math.abs(x)) dir = y < 0 ? 'FORWARD' : 'REVERSE';
    else                            dir = x < 0 ? 'LEFT'    : 'RIGHT';
  }

  return (
    <div className={s.wrap}>
      <div className={s.base} ref={baseRef} title="Drag to steer">
        <div className={s.r1}/><div className={s.r2}/><div className={s.r3}/>
        <div className={s.axH}/><div className={s.axV}/>
              <span className={`${s.lbl} ${s.lN}`} style={{color:'#f8f8f8'}}>FWD</span>
              <span className={`${s.lbl} ${s.lS}`} style={{color:'#f8f8f8'}}>REV</span>
              <span className={`${s.lbl} ${s.lW}`} style={{color:'#f8f8f8'}}>L</span>
              <span className={`${s.lbl} ${s.lE}`} style={{color:'#f8f8f8'}}>R</span>
        <div className={`${s.thumb} ${active ? s.active : ''}`}
          style={{ left:`${thumb.x}%`, top:`${thumb.y}%` }}/>
      </div>
      <div className={s.badge}
        data-dir={dir.toLowerCase().replace('forward','fwd').replace('reverse','rev')}>
        {dir}
      </div>
      {!active && <div className={s.hint}>⊕ DRAG TO STEER</div>}
    </div>
  );
}
